import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Save, Plus, Trash2, Edit3, X, Building2, Star, Mail, ShieldCheck, Send, CheckCircle2, AlertTriangle, Terminal } from "lucide-react";
import api from "../../api";

const EMPTY = {
  name: "", street: "", zip: "", city: "", country: "CH", vat: "", vatEnabled: true, email: "", phone: "",
  iban: "", logoBase64: "", invoicePrefix: "RW-", nextInvoiceNumber: 1, nextOfferNumber: 1,
  paymentTerms: "Zahlbar innert 30 Tagen via beigefügtem QR-Code.",
  defaultVatRate: 8.1, currency: "CHF", language: "de", isDefault: false, order: 0,
  smtpHost: "", smtpPort: 587, smtpUser: "", smtpPassword: "", smtpUseTls: true,
};

export default function Companies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [smtpTestRecipient, setSmtpTestRecipient] = useState("");
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/companies");
      setItems(r.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { id, createdAt, ...rest } = editing;
      // ensure numbers
      rest.nextInvoiceNumber = Number(rest.nextInvoiceNumber) || 1;
      rest.nextOfferNumber = Number(rest.nextOfferNumber) || 1;
      rest.vatEnabled = editing.vatEnabled !== false;
      rest.defaultVatRate = rest.vatEnabled ? (parseFloat(rest.defaultVatRate) || 8.1) : 0;
      rest.smtpPort = Number(rest.smtpPort) || 587;
      rest.smtpUseTls = editing.smtpUseTls !== false;
      if (id) {
        const r = await api.put(`/admin/companies/${id}`, rest);
        setItems((arr) => arr.map((x) => (x.id === id ? r.data : x)));
      } else {
        const r = await api.post("/admin/companies", rest);
        setItems((arr) => [...arr, r.data]);
      }
      setEditing(null);
    } catch (e) { alert(e.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  const startEditing = (company) => {
    setEditing({ ...company });
    setSmtpTestRecipient(company.email || "");
    setSmtpTestResult(null);
  };

  const runSmtpTest = async () => {
    if (!editing) return;
    const recipient = (smtpTestRecipient || editing.email || "admin@redwork.ch").trim();
    if (!recipient || !recipient.includes("@")) {
      alert("Bitte eine gültige Test-Empfänger-E-Mail eingeben.");
      return;
    }
    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      const res = await api.post("/admin/smtp/test", {
        host: editing.smtpHost || "",
        port: Number(editing.smtpPort) || 587,
        user: editing.smtpUser || "",
        password: editing.smtpPassword || "",
        useTls: editing.smtpUseTls !== false,
        fromEmail: editing.email || editing.smtpUser || "",
        fromName: editing.name || "RedWORK Test",
        toEmail: recipient,
      });
      setSmtpTestResult(res.data);
    } catch (e) {
      setSmtpTestResult({
        ok: false,
        logs: [`❌ Verbindungsfehler: ${e.response?.data?.detail || e.message}`],
        error: e.response?.data?.detail || e.message,
      });
    } finally {
      setSmtpTesting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Firma wirklich löschen?")) return;
    await api.delete(`/admin/companies/${id}`);
    setItems((arr) => arr.filter((x) => x.id !== id));
  };

  const setDefault = async (id) => {
    await api.post(`/admin/companies/${id}/set-default`);
    setItems((arr) => arr.map((x) => ({ ...x, isDefault: x.id === id })));
  };

  const handleLogo = (file, setter) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setter(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div data-testid="companies-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0f172a]">Firmen / Mandanten</h1>
          <p className="text-[#64748b] mt-1">Mehrere Firmen mit eigenem Logo und IBAN für Rechnungen / Offerten.</p>
        </div>
        <button onClick={() => startEditing(EMPTY)} data-testid="new-company-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-sm">
          <Plus size={15} /> Neue Firma
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {loading ? (
          <div className="col-span-full p-12 text-center"><Loader2 className="animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="col-span-full p-12 text-center text-[#64748b] bg-white rounded-2xl">Noch keine Firma angelegt.</div>
        ) : items.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl shadow-card p-5 relative">
            {c.isDefault && (
              <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-1 rounded-full"><Star size={11} /> Standard</span>
            )}
            <div className="flex items-center gap-3 mb-3">
              {c.logoBase64 ? (
                <img src={c.logoBase64} alt="logo" className="w-12 h-12 object-contain bg-slate-50 rounded-lg p-1" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center"><Building2 size={20} className="text-[#64748b]" /></div>
              )}
              <div className="min-w-0">
                <div className="font-extrabold text-[#0f172a] truncate">{c.name}</div>
                <div className="text-xs text-[#64748b] truncate">{c.zip} {c.city}</div>
              </div>
            </div>
            <div className="text-xs text-[#64748b] space-y-1 mb-3 break-all">
              <div><b>E-Mail:</b> {c.email || "—"}</div>
              <div><b>IBAN:</b> {c.iban || "—"}</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <b>MwSt:</b>
                {c.vatEnabled !== false ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Aktiv ({c.defaultVatRate ?? 8.1}%) {c.vat ? `· ${c.vat}` : ""}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                    Deaktiviert (0% / Befreit)
                  </span>
                )}
              </div>
              <div><b>SMTP Absender:</b> {c.smtpHost ? `${c.smtpHost} (${c.smtpUser || c.email})` : "System-Standard"}</div>
              <div><b>Präfix:</b> {c.invoicePrefix} · Nächste RG: {c.nextInvoiceNumber}</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {!c.isDefault && (
                <button onClick={() => setDefault(c.id)} className="text-xs px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 font-bold">Als Standard</button>
              )}
              <button onClick={() => startEditing(c)} data-testid={`edit-company-${c.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-[#1E88E5] hover:text-white font-bold inline-flex items-center gap-1"><Edit3 size={12} /> Bearbeiten</button>
              <button onClick={() => remove(c.id)} className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-[#E63946] hover:text-white font-bold inline-flex items-center gap-1"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#0f172a] text-white p-5 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-2"><Building2 size={18} /><h2 className="text-lg font-extrabold">{editing.id ? "Firma bearbeiten" : "Neue Firma"}</h2></div>
              <button onClick={() => setEditing(null)} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Firmenname *"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="company-name" className={inp} /></Field>
                <Field label="IBAN *"><input value={editing.iban} onChange={(e) => setEditing({ ...editing, iban: e.target.value })} data-testid="company-iban" className={inp} placeholder="CH4431999123000889012" /></Field>
                <Field label="Strasse"><input value={editing.street} onChange={(e) => setEditing({ ...editing, street: e.target.value })} className={inp} /></Field>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="PLZ"><input value={editing.zip} onChange={(e) => setEditing({ ...editing, zip: e.target.value })} className={inp} /></Field>
                  <div className="col-span-2"><Field label="Ort"><input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} className={inp} /></Field></div>
                </div>
                <Field label="E-Mail"><input value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className={inp} /></Field>
                <Field label="Telefon"><input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className={inp} /></Field>
                <Field label="Land"><input value={editing.country} onChange={(e) => setEditing({ ...editing, country: e.target.value })} className={inp} /></Field>
              </div>

              {/* MwSt (Mehrwertsteuer) Bereich */}
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/80 space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-extrabold text-[#0f172a] flex items-center gap-2">
                      <span>Mehrwertsteuer (MwSt)</span>
                      {editing.vatEnabled !== false ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                          Aktiv
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full">
                          Deaktiviert (0%)
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#64748b] mt-0.5">
                      MwSt-Pflicht aktivieren oder deaktivieren (z.B. Kleinunternehmen / MwSt-befreit).
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      data-testid="company-vat-toggle"
                      checked={editing.vatEnabled !== false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setEditing({
                          ...editing,
                          vatEnabled: checked,
                          defaultVatRate: checked ? (editing.defaultVatRate || 8.1) : 0,
                        });
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>

                {editing.vatEnabled !== false ? (
                  <div className="grid md:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                    <Field label="MwSt-Nummer (UID)">
                      <input
                        value={editing.vat || ""}
                        onChange={(e) => setEditing({ ...editing, vat: e.target.value })}
                        placeholder="CHE-123.456.789 MWST"
                        data-testid="company-vat-input"
                        className={inp}
                      />
                    </Field>
                    <Field label="Standard-MwSt-Satz (%)">
                      <input
                        type="number"
                        step="0.1"
                        value={editing.defaultVatRate ?? 8.1}
                        onChange={(e) => setEditing({ ...editing, defaultVatRate: e.target.value })}
                        placeholder="8.1"
                        data-testid="company-vat-rate-input"
                        className={inp}
                      />
                    </Field>
                  </div>
                ) : (
                  <div className="text-xs text-slate-600 bg-amber-50/90 border border-amber-200/80 p-2.5 rounded-lg flex items-center gap-2">
                    <span className="text-amber-600 font-bold">ℹ️</span>
                    <span>Diese Firma ist <b>nicht MwSt-pflichtig</b>. Auf Rechnungen und Offerten wird automatisch <b>0% MwSt</b> angewendet und keine MwSt-Nummer ausgewiesen.</span>
                  </div>
                )}
              </div>

              {/* SMTP / E-Mail Absender Konfiguration */}
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/80 space-y-3">
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-[#E63946]" />
                  <div className="text-sm font-extrabold text-[#0f172a]">
                    E-Mail & SMTP Absender-Konfiguration
                  </div>
                </div>
                <p className="text-xs text-[#64748b]">
                  Wenn Sie Rechnungen, Mahnungen oder Antworten für diese Firma versenden, wird der Absender dynamisch angepasst. Geben Sie hier optionale eigene SMTP-Serverdaten an oder lassen Sie die Felder leer, um den Standard-Mailserver mit der Firmen-E-Mail (<span className="font-mono text-slate-700">{editing.email || "Absender"}</span>) zu nutzen.
                </p>
                <div className="grid md:grid-cols-3 gap-3 pt-1">
                  <div className="md:col-span-2">
                    <Field label="SMTP Server (Host)">
                      <input
                        value={editing.smtpHost || ""}
                        onChange={(e) => setEditing({ ...editing, smtpHost: e.target.value })}
                        placeholder="z.B. mail.redwork.ch oder smtp.office365.com"
                        className={inp}
                      />
                    </Field>
                  </div>
                  <div>
                    <Field label="SMTP Port">
                      <input
                        type="number"
                        value={editing.smtpPort ?? 587}
                        onChange={(e) => setEditing({ ...editing, smtpPort: e.target.value })}
                        placeholder="587"
                        className={inp}
                      />
                    </Field>
                  </div>
                  <Field label="SMTP Benutzer">
                    <input
                      value={editing.smtpUser || ""}
                      onChange={(e) => setEditing({ ...editing, smtpUser: e.target.value })}
                      placeholder="info@ihrefirma.ch"
                      className={inp}
                    />
                  </Field>
                  <Field label="SMTP Passwort">
                    <input
                      type="password"
                      value={editing.smtpPassword || ""}
                      onChange={(e) => setEditing({ ...editing, smtpPassword: e.target.value })}
                      placeholder="••••••••••••"
                      className={inp}
                    />
                  </Field>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={editing.smtpUseTls !== false}
                        onChange={(e) => setEditing({ ...editing, smtpUseTls: e.target.checked })}
                        className="w-4 h-4 accent-[#E63946]"
                      />
                      <span className="text-xs font-bold text-slate-700">TLS / STARTTLS aktiv</span>
                    </label>
                  </div>
                </div>

                {/* SMTP Test & Live Diagnose Bereich */}
                <div className="mt-4 pt-3 border-t border-slate-200/80 bg-white p-3.5 rounded-xl border">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Terminal size={15} className="text-[#1E88E5]" />
                      <span className="text-xs font-extrabold text-[#0f172a] uppercase">
                        SMTP-Verbindungstest & Live-Diagnose
                      </span>
                    </div>
                    <span className="text-[11px] text-[#64748b]">
                      Port 465 (SSL) oder Port 587 (STARTTLS)
                    </span>
                  </div>

                  <p className="text-xs text-[#64748b] mt-1">
                    Senden Sie eine echte Testnachricht an Ihre E-Mail-Adresse, um Handshake, Authentifizierung und Zustellbarkeit sofort zu überprüfen.
                  </p>

                  <div className="flex items-center gap-2 mt-3 flex-wrap sm:flex-nowrap">
                    <input
                      type="email"
                      value={smtpTestRecipient}
                      onChange={(e) => setSmtpTestRecipient(e.target.value)}
                      placeholder="Empfänger für Test-E-Mail (z.B. ihr.name@domain.ch)"
                      className="grow px-3 py-2 rounded-lg border border-slate-200 focus:border-[#1E88E5] focus:outline-none text-xs text-[#0f172a] bg-slate-50"
                    />
                    <button
                      type="button"
                      onClick={runSmtpTest}
                      disabled={smtpTesting || (!editing.smtpHost && !editing.email)}
                      className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1E88E5] hover:bg-[#1976D2] disabled:opacity-50 text-white font-bold text-xs shadow-sm transition"
                    >
                      {smtpTesting ? (
                        <>
                          <Loader2 size={13} className="animate-spin" />
                          <span>Wird getestet...</span>
                        </>
                      ) : (
                        <>
                          <Send size={13} />
                          <span>Verbindung testen</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Test Result Diagnostics Console */}
                  {smtpTestResult && (
                    <div className="mt-3 text-xs rounded-xl p-3 bg-slate-900 text-slate-200 font-mono space-y-1 shadow-inner">
                      <div className="flex items-center justify-between pb-1 mb-1 border-b border-slate-700">
                        <span className="font-bold flex items-center gap-1">
                          {smtpTestResult.ok ? (
                            <span className="text-emerald-400 flex items-center gap-1">
                              <CheckCircle2 size={13} /> TEST ERFOLGREICH (250 OK)
                            </span>
                          ) : (
                            <span className="text-red-400 flex items-center gap-1">
                              <AlertTriangle size={13} /> VERBINDUNG FEHLGESCHLAGEN
                            </span>
                          )}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          {smtpTestResult.host}:{smtpTestResult.port}
                        </span>
                      </div>
                      <div className="space-y-0.5 text-[11px] leading-relaxed max-h-48 overflow-y-auto">
                        {(smtpTestResult.logs || []).map((log, lIdx) => (
                          <div key={lIdx} className={log.startsWith("❌") ? "text-red-400 font-bold" : (log.startsWith("✓") || log.startsWith("🎉")) ? "text-emerald-300 font-bold" : "text-slate-300"}>
                            {log}
                          </div>
                        ))}
                      </div>
                      {smtpTestResult.ok && (
                        <div className="text-[11px] text-emerald-400 mt-2 pt-1 border-t border-slate-800">
                          ✓ Die E-Mail wurde erfolgreich an <b>{smtpTestResult.to}</b> versendet! Prüfen Sie Ihr Postfach (und Spam-Ordner).
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <Field label="Logo (PNG / JPG, max. 1 MB)">
                <div className="flex items-center gap-3">
                  {editing.logoBase64 && <img src={editing.logoBase64} alt="logo preview" className="w-16 h-16 object-contain bg-slate-50 rounded-lg p-1 border border-slate-200" />}
                  <label className="px-4 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0f172a] text-sm font-bold cursor-pointer">
                    {editing.logoBase64 ? "Logo ersetzen" : "Logo hochladen"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogo(e.target.files?.[0], (b64) => setEditing((d) => ({ ...d, logoBase64: b64 })))} data-testid="company-logo-upload" />
                  </label>
                  {editing.logoBase64 && <button onClick={() => setEditing((d) => ({ ...d, logoBase64: "" }))} className="text-xs text-[#E63946] font-bold">Entfernen</button>}
                </div>
              </Field>

              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Rechnungs-Präfix"><input value={editing.invoicePrefix} onChange={(e) => setEditing({ ...editing, invoicePrefix: e.target.value })} className={inp} /></Field>
                <Field label="Nächste Rechnungsnummer"><input type="number" value={editing.nextInvoiceNumber} onChange={(e) => setEditing({ ...editing, nextInvoiceNumber: e.target.value })} className={inp} /></Field>
                <Field label="Nächste Offerten-Nummer"><input type="number" value={editing.nextOfferNumber} onChange={(e) => setEditing({ ...editing, nextOfferNumber: e.target.value })} className={inp} /></Field>
                <Field label="Währung"><input value={editing.currency} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} className={inp} /></Field>
                <Field label="Sprache"><select value={editing.language} onChange={(e) => setEditing({ ...editing, language: e.target.value })} className={inp}><option value="de">Deutsch</option><option value="fr">Französisch</option><option value="it">Italienisch</option><option value="en">Englisch</option></select></Field>
              </div>
              <Field label="Zahlungsbedingungen"><textarea value={editing.paymentTerms} onChange={(e) => setEditing({ ...editing, paymentTerms: e.target.value })} rows={2} className={inp} /></Field>

              <label className="flex items-center gap-2 select-none">
                <input type="checkbox" checked={!!editing.isDefault} onChange={(e) => setEditing({ ...editing, isDefault: e.target.checked })} className="w-4 h-4 accent-[#E63946]" />
                <span className="text-sm font-semibold">Als Standard-Firma verwenden</span>
              </label>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                <button onClick={() => setEditing(null)} className="px-4 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0f172a] font-bold text-sm">Abbrechen</button>
                <button onClick={save} disabled={saving || !editing.name} data-testid="save-company-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#E63946] hover:bg-[#c5303d] disabled:opacity-50 text-white font-bold text-sm">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Speichern
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#1E88E5] focus:outline-none text-sm bg-white text-[#0f172a]";
function Field({ label, children }) {
  return <label className="block"><span className="block text-xs font-bold uppercase tracking-wider text-[#64748b] mb-1.5">{label}</span>{children}</label>;
}
